/**
 * <hy-cart-drawer>: natives <dialog> + showModal() für sections/hy-cart-drawer.liquid.
 * Fokus-Falle, Escape und Hintergrund-Inertheit sind Plattformverhalten,
 * dasselbe Muster wie der Zoom-Dialog aus M3 AP-53. Ohne dieses Modul bleibt
 * das <dialog> per UA-Stylesheet geschlossen (display: none) — der
 * Warenkorb-Link im Header bleibt ein echter Link auf routes.cart_url, die
 * JS-freie Kauf-Grundfunktion läuft vollständig über die Warenkorbseite.
 *
 * Anfangsfokus liegt auf der Überschrift (tabindex="-1" + autofocus,
 * sections/hy-cart-drawer.liquid), nicht auf dem Schließen-Button —
 * WAI-ARIA-APG, Muster "Dialog (Modal)": bei Inhalten, die als Liste/
 * mehrere Absätze wahrgenommen werden müssen, gehört der Anfangsfokus auf
 * ein statisches Element am Kopf des Inhalts.
 *
 * Untersucht und abgeschlossen (AP-69, ROADMAP.md): Ein zunächst vermuteter
 * Zusammenhang mit einem skriptgesteuerten .focus()-Aufruf nach showModal()
 * erwies sich als falsch — derselbe Effekt (erster Tab-Druck nach dem
 * Öffnen bewegt den Fokus nicht, zweiter Druck funktioniert) trat live
 * identisch auf, obwohl hier bewusst kein .focus()-Aufruf mehr steht und
 * die Fokussierung vollständig nativ über autofocus läuft. Ursache ist
 * offenbar allgemeiner: tabindex="-1" als initialer Fokuspunkt eines
 * modalen Dialogs verzögert die interne Tab-Navigationssynchronisation um
 * einen Tastendruck, unabhängig vom Fokussierungsweg. Bewusst akzeptiert,
 * nicht umgangen — kein WCAG-Verstoß, Begründung siehe
 * sections/hy-cart-drawer.liquid.
 *
 * Mengenänderung/Entfernen (AP-65): POST an /cart/change.js mit line
 * (1-basierter Positionsindex, nicht dem instabilen line_item.key — der
 * ändert sich laut Shopifys eigener Dokumentation mit jeder Eigenschafts-
 * änderung der Zeile) und bundled section rendering für Header und Drawer
 * in einem einzigen Request. Kein Client-seitiges Preis-Rechnen (Regel 8):
 * jeder angezeigte Betrag stammt aus der Serverantwort.
 *
 * Bewusster Gegensatz zum No-JS-Formular-Fallback (M10, AP-167/B8): dessen
 * updates[]-Mengenfeld in snippets/hy-cart-line.liquid nutzt item.key, nicht
 * den Positionsindex — kein Widerspruch, sondern zwei unterschiedliche
 * API-Flächen mit unterschiedlichem Zeitfenster. Dieser AJAX-Pfad hier
 * spricht /cart/change.js unmittelbar nach dem Klick an (der Index ist in
 * diesem kurzen Fenster stabil genug); der klassische Formular-POST kann
 * dagegen erst nach beliebig langer Verweildauer der Seite im Tab
 * abgeschickt werden, wo ein veralteter Index die falsche Zeile treffen
 * könnte — ein veralteter Key führt im schlimmsten Fall zu einem No-op,
 * nie zur Falschadressierung.
 *
 * hy:cart:updated (aus assets/hy-product-form.js) aktualisiert die
 * Zeilenliste unabhängig davon, ob der Drawer sich öffnet — sonst zeigt ein
 * später manuell geöffneter Drawer noch den Stand vom Seitenaufruf.
 */
/**
 * Maximum quantity per order (AP-295). snippets/hy-quantity-input.liquid puts
 * the limit and the room left for this line (limit minus every other line of
 * the same product) on the field; snippets/hy-quantity-limit-note.liquid
 * renders the hidden note in the line. Same helpers as assets/hy-cart-page.js
 * — duplicated on purpose (tools/check-budgets.mjs follows no imports).
 */
function limitRoom(input) {
  if (!input || input.dataset.hyLimit === undefined) return Infinity;
  return Number(input.dataset.hyLimitRoom);
}

function revealLimitNote(row) {
  row?.querySelector('[data-hy-quantity-limit-note] > [hidden]')?.removeAttribute('hidden');
}

class HyCartDrawer extends HTMLElement {
  #controller = null;
  #dialog = null;
  #trigger = null;
  #pending = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const sectionId = this.dataset.sectionId;
    this.#dialog = this.querySelector('.hy-cart-drawer');
    if (!this.#dialog) return;

    // Delegiert auf document statt eine einzelne Referenz in connectedCallback
    // zu binden: hy-product-form.js ersetzt .hy-header__cart-link bei jedem
    // Add-to-Cart komplett (neuer Knoten aus der Section-Rendering-Antwort,
    // damit Zahl und aria-label zusammen aktualisiert werden) — eine einmalig
    // gebundene Referenz wäre nach dem ersten Kauf verwaist, ihr Klick-
    // Listener liefe ins Leere.
    document.addEventListener(
      'click',
      (event) => {
        const cartLink = event.target.closest('.hy-header__cart-link');
        if (!cartLink) return;
        event.preventDefault();
        this.#open(cartLink);
      },
      { signal },
    );

    this.querySelector('.hy-cart-drawer__close')?.addEventListener(
      'click',
      () => this.#dialog.close(),
      { signal },
    );

    // Backdrop-Klick schließt — dasselbe Muster wie der Zoom-Dialog aus
    // AP-53: nur wenn das Klickziel das <dialog>-Element selbst ist (der
    // Bereich außerhalb von .hy-cart-drawer__panel), nicht sein Inhalt.
    this.#dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.#dialog) this.#dialog.close();
      },
      { signal },
    );

    this.#dialog.addEventListener('close', this.#handleClose, { signal });

    // Klick-Delegation für Menge +/- und Entfernen — überlebt jeden
    // #applySections-Austausch, weil sie auf dem Dialog selbst sitzt, nicht
    // auf den ausgetauschten Zeilen-Knoten.
    this.#dialog.addEventListener('click', this.#handleMutationClick, { signal });
    this.#dialog.addEventListener('change', this.#handleQuantityChange, { signal });

    document.addEventListener(
      'hy:cart:updated',
      (event) => {
        // Inhalt immer aktualisieren, unabhängig vom Öffnen — sonst zeigt
        // ein später manuell geöffneter Drawer noch den Stand vom
        // Seitenaufruf, obwohl zwischenzeitlich per Formular gekauft wurde.
        this.#applySections(event.detail?.sections);

        if (this.dataset.openOnAdd !== 'true') return;
        if (document.querySelector('.hy-main-cart')) return; // nie auf /cart
        const title = event.detail?.cart?.title;
        // event.detail.trigger ist der auslösende Kaufbutton (AP-72) — ohne
        // ihn läge der Fokus nach dem Schließen bei mehreren Formularen auf
        // der Seite (M5: Quick Add auf jeder Produktkarte) immer auf dem
        // ERSTEN Formular, nicht auf dem tatsächlich geklickten.
        // isConnected schützt gegen einen verwaisten Knoten: hy-variant-
        // picker.js ersetzt das PDP-Formular per replaceWith, ein
        // zwischengespeicherter Trigger könnte sonst außerhalb des
        // Dokuments liegen.
        const trigger = event.detail?.trigger;
        this.#open(trigger?.isConnected ? trigger : document.querySelector('button[name="add"]'), title);
      },
      { signal },
    );

    document.addEventListener('hy:quantity-limit:reached', this.#handleLimitReached, { signal });

    // Theme-Editor: Sektion auswählen öffnet den Drawer zur Vorschau,
    // Abwählen schließt ihn wieder. Beide Events feuern nur im Editor,
    // kein request.design_mode-Guard nötig.
    document.addEventListener(
      'shopify:section:select',
      (event) => {
        if (event.detail?.sectionId === sectionId) this.#open(null);
      },
      { signal },
    );
    document.addEventListener(
      'shopify:section:deselect',
      (event) => {
        if (event.detail?.sectionId === sectionId) this.#dialog.close();
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #open(trigger, introItemTitle) {
    if (!this.#dialog || this.#dialog.open) return;

    this.#trigger = trigger;

    const intro = this.querySelector('[id^="HyCartDrawerIntro-"]');
    if (intro) {
      intro.textContent = introItemTitle
        ? this.dataset.addedPrefix + introItemTitle + this.dataset.addedSuffix
        : '';
    }

    document.documentElement.style.overflow = 'hidden';
    // Kein eigener .focus()-Aufruf hier — siehe Modul-Kommentar oben.
    // `autofocus` auf der Überschrift (sections/hy-cart-drawer.liquid)
    // übernimmt das nativ, als Teil dieses einen showModal()-Aufrufs.
    this.#dialog.showModal();

    // GA4 view_cart (M6 AP-95): assets/hy-datalayer.js liest die
    // [data-hy-dl-line]-Inseln aus diesem Element.
    if (this.querySelector('[data-hy-dl-line]')) {
      document.dispatchEvent(
        new CustomEvent('hy:cart:viewed', { detail: { container: this } }),
      );
    }
  }

  #handleClose = () => {
    document.documentElement.style.overflow = '';

    const intro = this.querySelector('[id^="HyCartDrawerIntro-"]');
    if (intro) intro.textContent = '';

    this.#trigger?.focus();
    this.#trigger = null;
  };

  #handleMutationClick = (event) => {
    const removeButton = event.target.closest('.hy-cart-drawer__remove');
    const minusButton = event.target.closest('.hy-cart-drawer__quantity-minus');
    const plusButton = event.target.closest('.hy-cart-drawer__quantity-plus');
    const button = removeButton || minusButton || plusButton;
    if (!button) return;

    const row = button.closest('.hy-cart-drawer__item');
    const line = Number(row?.dataset.hyLine);
    if (!line) return;

    if (removeButton) {
      const title = row.querySelector('.hy-cart-drawer__item-title')?.textContent || '';
      // GA4 remove_from_cart (M6 AP-95): das Item der Zeile JETZT lesen — nach
      // dem /cart/change.js-Austausch ist die Zeile weg.
      const dl = row.querySelector('[data-hy-dl-line]');
      if (dl) {
        try {
          document.dispatchEvent(
            new CustomEvent('hy:cart:removed', {
              detail: { item: JSON.parse(dl.textContent), quantity: Number(row.querySelector('.hy-cart-drawer__quantity-input')?.value) || undefined },
            }),
          );
        } catch (error) {
          /* defekte Insel: kein Event, kein Bruch der Entfernen-Funktion */
        }
      }
      this.#mutate(line, 0, button, { removed: true, title });
      return;
    }

    const input = row.querySelector('.hy-cart-drawer__quantity-input');
    if (!input) return;
    const step = Number(input.step) || 1;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const current = Number(input.value) || min;
    // At the maximum quantity per order plus shows the note instead (AP-295).
    if (plusButton && current + step > limitRoom(input)) {
      revealLimitNote(row);
      return;
    }
    const next = minusButton
      ? Math.max(min, current - step)
      : Math.min(max, current + step);
    if (next === current) return;
    this.#mutate(line, next, button, { removed: false });
  };

  #handleQuantityChange = (event) => {
    const input = event.target.closest('.hy-cart-drawer__quantity-input');
    if (!input) return;

    const row = input.closest('.hy-cart-drawer__item');
    const line = Number(row?.dataset.hyLine);
    if (!line) return;

    if (this.#pending) return;
    let quantity = Math.max(0, Number(input.value) || 0);
    // Above the maximum quantity: save the highest allowed quantity instead
    // and show the note (AP-295). Nothing to save when the line already has it.
    const room = limitRoom(input);
    const limitReached = quantity > room;
    if (limitReached) {
      quantity = Math.max(room, Number(input.min) || 1);
      input.value = String(quantity);
      if (quantity === Number(input.defaultValue)) {
        revealLimitNote(row);
        return;
      }
    }
    this.#mutate(line, quantity, input, { removed: quantity === 0, limitReached });
  };

  /**
   * Quick add of a product card at its maximum quantity (AP-295,
   * assets/hy-product-form.js): nothing was added; open the drawer and show
   * the note at the product's line instead.
   */
  #handleLimitReached = (event) => {
    if (document.querySelector('.hy-main-cart')) return;
    const productId = String(event.detail?.productId || '');
    const row = this.querySelector(`.hy-cart-drawer__item[data-hy-product-id="${CSS.escape(productId)}"]`);
    if (!row) return;
    event.preventDefault();
    const trigger = event.detail?.trigger;
    this.#open(trigger?.isConnected ? trigger : null);
    revealLimitNote(row);
  };

  #mutate(line, quantity, control, { removed, title, limitReached } = {}) {
    if (this.#pending) return;
    this.#pending = true;

    control?.setAttribute('aria-busy', 'true');

    const body = new URLSearchParams();
    body.append('line', String(line));
    body.append('quantity', String(quantity));
    body.append('sections', this.#sectionIds().join(','));
    body.append('sections_url', window.location.pathname + window.location.search);

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: body.toString(),
    })
      .then(async (response) => {
        const responseJson = await response.json();

        if (!response.ok) {
          this.#showStatus(responseJson.description || responseJson.message);
          return;
        }

        this.#applySections(responseJson.sections);
        this.#showStatus(
          removed
            ? this.dataset.removedPrefix + title + this.dataset.removedSuffix
            : this.dataset.quantityUpdated,
        );
        this.#restoreFocus(line, removed);

        document.dispatchEvent(
          new CustomEvent('hy:cart:updated', {
            detail: { cart: responseJson, sections: responseJson.sections },
          }),
        );
        // After the event: its listeners swap the cart markup once more
        // (the drawer updates the cart page and itself), which would hide the
        // note again (AP-295).
        if (limitReached) {
          revealLimitNote(this.querySelector(`.hy-cart-drawer__item[data-hy-line="${line}"]`));
        }
      })
      .catch(() => {
        window.location.assign('/cart');
      })
      .finally(() => {
        control?.removeAttribute('aria-busy');
        this.#pending = false;
      });
  }

  #sectionIds() {
    const ids = [];
    const headerId = document.querySelector('.hy-header')?.id.replace('shopify-section-', '');
    if (headerId) ids.push(headerId);
    if (this.dataset.sectionId) ids.push(this.dataset.sectionId);
    // Steht der Drawer ausnahmsweise auf /cart offen (der Warenkorb-Link
    // funktioniert dort genauso), hält das die Tabelle synchron mit — ohne
    // eigenes Gewicht, wenn hy-main-cart.liquid gar nicht auf der Seite ist.
    const cartPageId = document.querySelector('hy-cart-page')?.dataset.sectionId;
    if (cartPageId) ids.push(cartPageId);
    return ids;
  }

  #applySections(sections) {
    if (!sections) return;

    const headerId = document.querySelector('.hy-header')?.id.replace('shopify-section-', '');
    if (headerId && sections[headerId]) {
      const headerDoc = new DOMParser().parseFromString(sections[headerId], 'text/html');
      const newCartLink = headerDoc.querySelector('.hy-header__cart-link');
      const currentCartLink = document.querySelector('.hy-header__cart-link');
      if (newCartLink && currentCartLink) currentCartLink.replaceWith(newCartLink);
    }

    const ownHtml = sections[this.dataset.sectionId];
    if (ownHtml) {
      const ownDoc = new DOMParser().parseFromString(ownHtml, 'text/html');

      const newItems = ownDoc.querySelector('.hy-cart-drawer__items');
      const oldItems = this.querySelector('.hy-cart-drawer__items');
      if (newItems && oldItems) oldItems.replaceWith(newItems);

      const newSummary = ownDoc.querySelector('.hy-cart-drawer__summary');
      const oldSummary = this.querySelector('.hy-cart-drawer__summary');
      // The server omits __summary for an empty cart, so the first add
      // after an empty cart has nothing to replace: insert it after the
      // (already swapped) item list instead.
      const currentItems = this.querySelector('.hy-cart-drawer__items');
      if (newSummary && oldSummary) oldSummary.replaceWith(newSummary);
      else if (newSummary && currentItems) currentItems.after(newSummary);
      else if (!newSummary && oldSummary) oldSummary.remove();

      // Item count next to the title (drawer style "modern", AP-274): it
      // sits in the header, outside the two swapped areas. Absent in the
      // classic markup, then nothing happens.
      const newCount = ownDoc.querySelector('[data-hy-cart-drawer-count]');
      const oldCount = this.querySelector('[data-hy-cart-drawer-count]');
      if (newCount && oldCount) oldCount.textContent = newCount.textContent;
    }

    // Steht die Warenkorbseite selbst im DOM (Mutation kam von dort, oder
    // der Drawer wurde ausnahmsweise auf /cart bedient), deren Tabelle
    // ebenfalls aktualisieren — assets/hy-cart-page.js macht dasselbe für
    // den umgekehrten Fall.
    const cartPage = document.querySelector('hy-cart-page');
    const cartPageHtml = cartPage && sections[cartPage.dataset.sectionId];
    if (cartPageHtml) {
      const cartPageDoc = new DOMParser().parseFromString(cartPageHtml, 'text/html');
      const newContent = cartPageDoc.querySelector('.hy-main-cart__content');
      const oldContent = cartPage.querySelector('.hy-main-cart__content');
      if (newContent && oldContent) oldContent.replaceWith(newContent);
    }
  }

  #restoreFocus(line, wasRemoval) {
    // Ein Frame verzögert: live beobachtet, dass ein synchroner focus()-
    // Aufruf auf ein Element INNERHALB des per replaceWith() gerade erst
    // eingefügten Teilbaums (aus einem per DOMParser geparsten, fremden
    // Document adoptiert) auf document.body zurückfiel, obwohl derselbe
    // Aufruf auf ein unverändertes Element (den Schließen-Button)
    // zuverlässig funktionierte — ein frisch eingefügter Knoten scheint
    // erst nach einem Layout-Durchlauf zuverlässig fokussierbar zu sein.
    // Nach einem Frame bleibt der eigene focus()-Aufruf bestehen.
    requestAnimationFrame(() => {
      const items = Array.from(this.querySelectorAll('.hy-cart-drawer__item'));

      if (items.length === 0) {
        this.querySelector('.hy-cart-drawer__close')?.focus();
        return;
      }

      // Nach einer Entfernung rückt die nächste Zeile auf denselben Index
      // nach (data-hy-line wird bei jedem Austausch aus dem neuen
      // cart.items neu vergeben) — war die entfernte Zeile die letzte, gibt
      // es diesen Index nicht mehr, dann die neue letzte Zeile.
      const target =
        items.find((el) => Number(el.dataset.hyLine) === line) || items[items.length - 1];
      const control = wasRemoval
        ? target.querySelector('.hy-cart-drawer__remove')
        : target.querySelector('.hy-cart-drawer__quantity-input');
      control?.focus();

      // Absoluter Fallback: Landet der Fokus trotzdem außerhalb des
      // Drawers (z. B. weil target/control aus einem unerwarteten Grund
      // fehlt), auf den Schließen-Button ausweichen statt document.body zu
      // überlassen — ein offener modaler Dialog darf den Fokus nie
      // verlieren.
      if (!this.contains(document.activeElement)) {
        this.querySelector('.hy-cart-drawer__close')?.focus();
      }
    });
  }

  #showStatus(message) {
    const statusEl = this.querySelector('.hy-cart-drawer__status');
    if (statusEl) statusEl.textContent = message || '';
  }
}

customElements.define('hy-cart-drawer', HyCartDrawer);
